import { useState, useRef, useCallback } from 'react';

/**
 * Sequences dock mode transitions: exit animation plays first,
 * then the mode commits, then the enter animation plays.
 *
 * Usage:
 *   const { mode, isExiting, switchTo, onExitEnd } = useAnimatedSwitch('dock');
 *
 *   switchTo('type')          ← triggers exit on current component
 *   onExitEnd()               ← call from onAnimationComplete / onAnimationEnd
 *                                commits the mode, new component mounts with enter animation
 */
export function useAnimatedSwitch<T>(initial: T) {
  const [mode, setMode] = useState<T>(initial);
  const [isExiting, setIsExiting] = useState(false);
  const pending = useRef<T | null>(null);

  const switchTo = useCallback((next: T) => {
    pending.current = next;
    setIsExiting(true);
  }, []);

  /** Commit a mode change immediately, skipping exit animation. */
  const commitNow = useCallback((next: T) => {
    pending.current = null;
    setIsExiting(false);
    setMode(next);
  }, []);

  const onExitEnd = useCallback(() => {
    if (pending.current != null) {
      setMode(pending.current);
      pending.current = null;
    }
    setIsExiting(false);
  }, []);

  return { mode, isExiting, switchTo, commitNow, onExitEnd };
}
